import React, { useState, useEffect, useCallback } from 'react'
import { Table } from 'react-bootstrap'
import { FaSearch } from 'react-icons/fa'
import API from '../../../service/api'
import { customToast } from '../../../utils/toasts'
import './UserManagement.css'

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [searchKey, setSearchKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 15
  const [filter, setFilter] = useState('all') // all, active, inactive, deleted

  const [editModal, setEditModal] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [addModal, setAddModal] = useState(false)
  const [addForm, setAddForm] = useState({ name: '', email: '', mobileNumber: '', location: '', userRole: '', departmentId: '', ci: '', rId: '' })
  const [roles, setRoles] = useState([])
  const [departments, setDepartments] = useState([])

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      const res = await API({ url: '/user/all-users-manage', method: 'GET', params: { searchKey }, manageLoader: false })
      setUsers(res?.data?.data || [])
    } catch (err) {
      console.error('Failed to fetch users', err)
    } finally {
      setLoading(false)
    }
  }, [searchKey])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers()
    }, 400)
    return () => clearTimeout(timer)
  }, [fetchUsers])

  const fetchRolesAndDepartments = async () => {
    try {
      const res = await API({ url: '/user/roles-departments', method: 'GET', manageLoader: false })
      if (res?.data?.data) {
        setRoles(res.data.data.roles || [])
        setDepartments(res.data.data.departments || [])
      }
    } catch (err) {
      console.error('Failed to fetch roles/departments', err)
    }
  }

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate'
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return
    try {
      const res = await API({ url: `/user/toggle-status/${userId}`, method: 'PUT', manageLoader: false })
      if (res?.data?.data) {
        customToast({ message: `User ${action}d successfully` })
        fetchUsers()
      }
    } catch (err) {
      console.error('Toggle status failed', err)
    }
  }

  const openEditModal = (user) => {
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      mobileNumber: user.mobileNumber || '',
      location: user.location || '',
      userRole: user.userRole || '',
      departmentId: user.departmentId || '',
      isActive: user.isActive ?? true,
      isDeleted: user.isDeleted ?? false,
      status: user.status || '',
      ci: user.ci || '',
      rId: user.rId || '',
      isVerified: user.isVerified ?? false,
      isAddressVerified: user.isAddressVerified ?? false,
      isLocked: user.isLocked ?? false,
      isParallelSessionAllowed: user.isParallelSessionAllowed ?? false,
      failedLoginAttempts: user.failedLoginAttempts ?? 0,
      assignedAccessTemplateId: user.assignedAccessTemplateId || ''
    })
    setEditModal(user)
    if (roles.length === 0) fetchRolesAndDepartments()
  }

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  const handleResetPassword = async () => {
    if (!window.confirm(`Reset password for ${editModal.email} to default (Magenta@123)?`)) return
    try {
      const res = await API({ url: `/user/reset-password/${editModal._id}`, method: 'PUT', manageLoader: false })
      if (res?.data?.data) {
        customToast({ message: 'Password reset to default successfully' })
      }
    } catch (err) {
      console.error('Reset password failed', err)
    }
  }

  const saveEditUser = async () => {
    try {
      const res = await API({ url: `/user/update-manage/${editModal._id}`, method: 'PUT', data: editForm, manageLoader: false })
      if (res?.data?.data) {
        customToast({ message: 'User updated successfully' })
        setEditModal(null)
        fetchUsers()
      }
    } catch (err) {
      console.error('Update failed', err)
    }
  }

  const openAddModal = () => {
    setAddForm({ name: '', email: '', mobileNumber: '', location: '', userRole: '', departmentId: '', ci: '', rId: '' })
    setAddModal(true)
    if (roles.length === 0) fetchRolesAndDepartments()
  }

  const handleAddFormChange = (field, value) => {
    setAddForm(prev => ({ ...prev, [field]: value }))
  }

  const saveAddUser = async () => {
    if (!addForm.name || !addForm.email || !addForm.userRole) {
      customToast({ message: 'Name, Email and Role are required', type: 'error' })
      return
    }
    try {
      const res = await API({ url: '/user/create-manage', method: 'POST', data: addForm, manageLoader: false })
      if (res?.data?.data) {
        customToast({ message: 'User created successfully' })
        setAddModal(false)
        fetchUsers()
      }
    } catch (err) {
      console.error('Create failed', err)
    }
  }

  const getTimeDiff = (lastActive) => {
    if (!lastActive) return 'Never'
    const diff = Math.floor((Date.now() - new Date(lastActive).getTime()) / 60000)
    if (diff < 1) return 'Just now'
    if (diff < 60) return `${diff}m ago`
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`
    return `${Math.floor(diff / 1440)}d ago`
  }

  const filteredUsers = users.filter(u => {
    if (filter === 'active') return u.isActive && !u.isDeleted
    if (filter === 'inactive') return !u.isActive && !u.isDeleted
    if (filter === 'deleted') return u.isDeleted
    return true
  })

  const activeCount = users.filter(u => u.isActive && !u.isDeleted).length
  const inactiveCount = users.filter(u => !u.isActive && !u.isDeleted).length
  const deletedCount = users.filter(u => u.isDeleted).length

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE)
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  return (
    <div className='um-container'>
      <div className='um-header'>
        <h5 className='um-title'>User Management</h5>
        <div className='um-stats'>
          <span className={`um-badge um-badge-active ${filter === 'active' ? 'um-badge-selected' : ''}`} onClick={() => { setFilter(f => f === 'active' ? 'all' : 'active'); setCurrentPage(1) }} style={{ cursor: 'pointer' }}>{activeCount} Active</span>
          <span className={`um-badge um-badge-inactive ${filter === 'inactive' ? 'um-badge-selected' : ''}`} onClick={() => { setFilter(f => f === 'inactive' ? 'all' : 'inactive'); setCurrentPage(1) }} style={{ cursor: 'pointer' }}>{inactiveCount} Inactive</span>
          <span className={`um-badge um-badge-deleted ${filter === 'deleted' ? 'um-badge-selected' : ''}`} onClick={() => { setFilter(f => f === 'deleted' ? 'all' : 'deleted'); setCurrentPage(1) }} style={{ cursor: 'pointer', background: deletedCount > 0 ? '#dc3545' : '#6c757d', color: '#fff' }}>{deletedCount} Deleted</span>
          <span className={`um-badge um-badge-total ${filter === 'all' ? 'um-badge-selected' : ''}`} onClick={() => { setFilter('all'); setCurrentPage(1) }} style={{ cursor: 'pointer' }}>{users.length} Total</span>
        </div>
      </div>

      <div className='um-toolbar'>
        <div className='um-search-box'>
          <FaSearch className='um-search-icon' />
          <input
            type='text'
            placeholder='Search by name, email or CI...'
            value={searchKey}
            onChange={(e) => { setSearchKey(e.target.value); setCurrentPage(1) }}
            className='um-search-input'
          />
        </div>
        <div className='um-toolbar-actions'>
          <button className='um-add-btn' onClick={openAddModal}>+ Add User</button>
          <button className='um-refresh-btn' onClick={fetchUsers} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className='um-table-wrapper'>
        <Table bordered hover size='sm' className='um-table'>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Role</th>
              <th>CI</th>
              <th>Department</th>
              <th>Location</th>
              <th>Status</th>
              <th>Last Active</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 && (
              <tr><td colSpan={11} className='text-center'>Loading...</td></tr>
            )}
            {!loading && filteredUsers.length === 0 && (
              <tr><td colSpan={11} className='text-center'>No users found</td></tr>
            )}
            {paginatedUsers.map((u, i) => (
              <tr key={u._id} className={u.isDeleted ? 'um-row-deleted' : (!u.isActive ? 'um-row-inactive' : '')}>
                <td>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                <td>{u.name || '-'}</td>
                <td>{u.email || '-'}</td>
                <td>{u.mobileNumber || '-'}</td>
                <td><span className='um-role-badge'>{u.roleName || '-'}</span></td>
                <td>{u.ci || '-'}</td>
                <td>{u.departmentName || '-'}</td>
                <td>{u.location || '-'}</td>
                <td>
                  {u.isDeleted
                    ? <span className='um-status um-status-deleted'>Deleted</span>
                    : <span className={`um-status ${u.isActive ? 'um-status-active' : 'um-status-inactive'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                  }
                </td>
                <td>{getTimeDiff(u.lastActiveAt)}</td>
                <td>
                  <button className='um-edit-btn' onClick={() => openEditModal(u)}>Edit</button>
                  {u.isDeleted
                    ? <button className='um-toggle-btn um-btn-restore' onClick={() => handleRestoreUser(u._id)}>Restore</button>
                    : <button
                        className={`um-toggle-btn ${u.isActive ? 'um-btn-deactivate' : 'um-btn-activate'}`}
                        onClick={() => handleToggleStatus(u._id, u.isActive)}
                      >
                        {u.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className='um-pagination'>
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
        </div>
      )}

      {/* Add User Modal */}
      {addModal && (
        <div className='um-modal-overlay' onClick={() => setAddModal(false)}>
          <div className='um-modal' onClick={e => e.stopPropagation()}>
            <div className='um-modal-header'>
              <h6>Add New User</h6>
              <button className='um-modal-close' onClick={() => setAddModal(false)}>&times;</button>
            </div>
            <div className='um-modal-body'>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Name *</label>
                  <input value={addForm.name} onChange={e => handleAddFormChange('name', e.target.value)} placeholder='Full name' />
                </div>
                <div className='um-form-group'>
                  <label>Email *</label>
                  <input value={addForm.email} onChange={e => handleAddFormChange('email', e.target.value)} placeholder='user@magentamobility.com' />
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Mobile Number</label>
                  <input value={addForm.mobileNumber} onChange={e => handleAddFormChange('mobileNumber', e.target.value)} placeholder='10-digit number' />
                </div>
                <div className='um-form-group'>
                  <label>Location</label>
                  <input value={addForm.location} onChange={e => handleAddFormChange('location', e.target.value)} placeholder='City' />
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>CI *</label>
                  <input value={addForm.ci} onChange={e => handleAddFormChange('ci', e.target.value)} placeholder='e.g. CA0001' />
                </div>
                <div className='um-form-group'>
                  <label>RID</label>
                  <input value={addForm.rId} onChange={e => handleAddFormChange('rId', e.target.value)} placeholder='e.g. DU016' />
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Role *</label>
                  <select value={addForm.userRole} onChange={e => handleAddFormChange('userRole', e.target.value)}>
                    <option value=''>Select Role</option>
                    {roles.map(r => (
                      <option key={r._id} value={r._id}>{r.label} ({r.value})</option>
                    ))}
                  </select>
                </div>
                <div className='um-form-group'>
                  <label>Department</label>
                  <select value={addForm.departmentId} onChange={e => handleAddFormChange('departmentId', e.target.value)}>
                    <option value=''>No Department</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className='um-default-pass-note'>Default password: Magenta@123 (user should change after first login)</p>
            </div>
            <div className='um-modal-footer'>
              <button className='um-btn-cancel' onClick={() => setAddModal(false)}>Cancel</button>
              <button className='um-btn-save' onClick={saveAddUser}>Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && (
        <div className='um-modal-overlay' onClick={() => setEditModal(null)}>
          <div className='um-modal' onClick={e => e.stopPropagation()}>
            <div className='um-modal-header'>
              <h6>Edit User - {editModal.email}</h6>
              <button className='um-modal-close' onClick={() => setEditModal(null)}>&times;</button>
            </div>
            <div className='um-modal-body'>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Name</label>
                  <input value={editForm.name} onChange={e => handleEditFormChange('name', e.target.value)} />
                </div>
                <div className='um-form-group'>
                  <label>Email</label>
                  <input value={editForm.email} disabled className='um-input-disabled' />
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Mobile Number</label>
                  <input value={editForm.mobileNumber} onChange={e => handleEditFormChange('mobileNumber', e.target.value)} />
                </div>
                <div className='um-form-group'>
                  <label>Location</label>
                  <input value={editForm.location} onChange={e => handleEditFormChange('location', e.target.value)} />
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>CI</label>
                  <input value={editForm.ci} onChange={e => handleEditFormChange('ci', e.target.value)} />
                </div>
                <div className='um-form-group'>
                  <label>RID</label>
                  <input value={editForm.rId} onChange={e => handleEditFormChange('rId', e.target.value)} />
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Role</label>
                  <select value={editForm.userRole} onChange={e => handleEditFormChange('userRole', e.target.value)}>
                    <option value=''>Select Role</option>
                    {roles.map(r => (
                      <option key={r._id} value={r._id}>{r.label} ({r.value})</option>
                    ))}
                  </select>
                </div>
                <div className='um-form-group'>
                  <label>Department</label>
                  <select value={editForm.departmentId} onChange={e => handleEditFormChange('departmentId', e.target.value)}>
                    <option value=''>No Department</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Status</label>
                  <select value={editForm.status} onChange={e => handleEditFormChange('status', e.target.value)}>
                    <option value='Pending RA Approval'>Pending RA Approval</option>
                    <option value='Approved'>Approved</option>
                    <option value='RA Approved'>RA Approved</option>
                    <option value='Rejected'>Rejected</option>
                  </select>
                </div>
                <div className='um-form-group'>
                  <label>Is Active</label>
                  <select value={editForm.isActive} onChange={e => handleEditFormChange('isActive', e.target.value === 'true')}>
                    <option value='true'>Active</option>
                    <option value='false'>Inactive</option>
                  </select>
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Is Deleted</label>
                  <select value={editForm.isDeleted} onChange={e => handleEditFormChange('isDeleted', e.target.value === 'true')}>
                    <option value='false'>No</option>
                    <option value='true'>Yes (Deleted)</option>
                  </select>
                </div>
                <div className='um-form-group'>
                  <label>Is Verified</label>
                  <select value={editForm.isVerified} onChange={e => handleEditFormChange('isVerified', e.target.value === 'true')}>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Is Address Verified</label>
                  <select value={editForm.isAddressVerified} onChange={e => handleEditFormChange('isAddressVerified', e.target.value === 'true')}>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </div>
                <div className='um-form-group'>
                  <label>Is Locked</label>
                  <select value={editForm.isLocked} onChange={e => handleEditFormChange('isLocked', e.target.value === 'true')}>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Parallel Session Allowed</label>
                  <select value={editForm.isParallelSessionAllowed} onChange={e => handleEditFormChange('isParallelSessionAllowed', e.target.value === 'true')}>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </div>
                <div className='um-form-group'>
                  <label>Failed Login Attempts</label>
                  <input type='number' value={editForm.failedLoginAttempts} onChange={e => handleEditFormChange('failedLoginAttempts', parseInt(e.target.value) || 0)} />
                </div>
              </div>
              <div className='um-form-row'>
                <div className='um-form-group'>
                  <label>Access Template ID</label>
                  <input value={editForm.assignedAccessTemplateId} onChange={e => handleEditFormChange('assignedAccessTemplateId', e.target.value)} />
                </div>
                <div className='um-form-group' />
              </div>
              {editModal && (
                <div className='um-info-section'>
                  <div className='um-form-row'>
                    <div className='um-form-group'>
                      <label>Pass Expiry Date</label>
                      <input value={editModal.passExpDate ? new Date(editModal.passExpDate).toLocaleDateString('en-IN') : '-'} disabled className='um-input-disabled' />
                    </div>
                    <div className='um-form-group'>
                      <label>Created At</label>
                      <input value={editModal.createdAt ? new Date(editModal.createdAt).toLocaleDateString('en-IN') : '-'} disabled className='um-input-disabled' />
                    </div>
                  </div>
                  <div className='um-form-row'>
                    <div className='um-form-group'>
                      <label>Updated At</label>
                      <input value={editModal.updatedAt ? new Date(editModal.updatedAt).toLocaleDateString('en-IN') : '-'} disabled className='um-input-disabled' />
                    </div>
                    <div className='um-form-group'>
                      <label>Group IDs</label>
                      <input value={editModal.groupIds?.length ? editModal.groupIds.join(', ') : '-'} disabled className='um-input-disabled' />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className='um-modal-footer'>
              <button className='um-btn-reset-pass' onClick={handleResetPassword}>Reset Password</button>
              <button className='um-btn-cancel' onClick={() => setEditModal(null)}>Cancel</button>
              <button className='um-btn-save' onClick={saveEditUser}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  async function handleRestoreUser (userId) {
    if (!window.confirm('Restore this deleted user?')) return
    try {
      const res = await API({ url: `/user/update-manage/${userId}`, method: 'PUT', data: { isDeleted: false, isActive: true, status: 'Approved' }, manageLoader: false })
      if (res?.data?.data) {
        customToast({ message: 'User restored successfully' })
        fetchUsers()
      }
    } catch (err) {
      console.error('Restore failed', err)
    }
  }
}

export default UserManagement
